/**
 * Login page — initiates PKCE authorization code flow.
 *
 * Protected routes can land here with autoStart=true to begin the redirect
 * immediately while still showing a visible loading or error state if auth
 * bootstrap fails.
 *
 * @module apps/web/pages/Login
 * @since W2.9
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout.js';
import { useAuth } from '../contexts/auth.js';
import { isPostLogout, clearPostLogout } from '../lib/auth.js';

interface LoginLocationState {
  returnTo?: string;
  autoStart?: boolean;
}

export function LoginPage() {
  const { login, authenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoStartedRef = useRef(false);
  const locationState = (location.state as LoginLocationState | null) ?? null;
  const returnTo = locationState?.returnTo ?? '/';
  const autoStart = locationState?.autoStart ?? false;

  // If user just logged out, suppress auto-start and always force prompt=login
  const postLogout = isPostLogout();

  const handleLogin = useCallback(async () => {
    setLoading(true);
    setError(null);
    clearPostLogout();

    try {
      // Always force the auth server to show the login form so the user
      // can see their username and switch accounts if needed
      await login(returnTo, { prompt: 'login' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
      setLoading(false);
    }
  }, [login, returnTo]);

  useEffect(() => {
    if (authenticated && !postLogout) {
      navigate(returnTo, { replace: true });
    }
  }, [authenticated, navigate, returnTo, postLogout]);

  useEffect(() => {
    // Do NOT auto-start if user just logged out
    if (!authenticated && autoStart && !autoStartedRef.current && !postLogout) {
      autoStartedRef.current = true;
      void handleLogin();
    }
  }, [authenticated, autoStart, handleLogin, postLogout]);

  if (authenticated) {
    return null;
  }

  return (
    <AuthLayout
      title="Sign in to rule42"
      subtitle="Continue to your workspace"
    >
      <div className="space-y-4">
        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-900/30 border border-red-800 text-red-300 text-sm">
            {error}
          </div>
        )}

        <button
          onClick={handleLogin}
          disabled={loading}
          className="btn-primary w-full"
        >
          {loading ? (
            <>
              <span className="w-4 h-4 border border-white/40 border-t-white rounded-full animate-spin" />
              Redirecting…
            </>
          ) : (
            <>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.5 6H14c-.28 0-.5.22-.5.5v3h3l-.47 3H13.5V21h-3v-8.5H9V9.5h1.5V8c0-1.93 1.57-3.5 3.5-3.5h2.5V8z" fill="currentColor"/>
              </svg>
              Continue with rule42
            </>
          )}
        </button>

        <div className="divider">
          <span className="text-xs text-slate-600">or</span>
        </div>

        <p className="text-center text-sm text-slate-400">
          Don&apos;t have an account?{' '}
          <a
            href="/register"
            className="text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            Sign up
          </a>
        </p>
      </div>
    </AuthLayout>
  );
}
